import styled from "styled-components";
import { motion } from "framer-motion";
import {
  Code,
  Layout,
  Server,
  Smartphone,
  Cloud,
  Briefcase,
} from "lucide-react";
import { useState } from "react";

/* ================= PAGE ================= */

const Page = styled.section`
  position: relative;
  padding: 180px 20px;
  background: radial-gradient(
      900px at 50% 0%,
      rgba(59, 130, 246, 0.14),
      transparent 55%
    ),
    ${({ theme }) => theme.colors.bg};
  overflow: hidden;
`;

/* ================= CONTAINER ================= */

const Container = styled.div`
  max-width: 1200px;
  margin: auto;
  position: relative;
`;

/* ================= HEADER ================= */

const Heading = styled(motion.h1)`
  text-align: center;
  font-size: clamp(36px, 5vw, 56px);
  font-weight: 900;
  color: ${({ theme }) => theme.colors.text};
`;

const Sub = styled(motion.p)`
  text-align: center;
  max-width: 820px;
  margin: 22px auto 0;
  font-size: 18px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ================= FLOW WRAPPER ================= */

const FlowWrap = styled.div`
  position: relative;
  margin-top: 140px;
`;

/* ================= GRID ================= */

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 60px;
  position: relative;
  z-index: 2;

  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
  }
`;

/* ================= CONNECTORS ================= */

const HorizontalConnector = styled.div`
  position: absolute;
  top: 110px;
  left: 0;
  right: 0;
  height: 2px;
  background-image: linear-gradient(
    to right,
    rgba(59, 130, 246, 0.45) 40%,
    rgba(59, 130, 246, 0) 0%
  );
  background-size: 18px 2px;
  background-repeat: repeat-x;
  animation: dash 7s linear infinite;

  @keyframes dash {
    from {
      background-position: 0 0;
    }
    to {
      background-position: 240px 0;
    }
  }

  @media (max-width: 1000px) {
    display: none;
  }
`;

/* ================= CARD ================= */

const Card = styled(motion.div)`
  position: relative;
  padding: 54px 44px;
  border-radius: 30px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.035),
    rgba(255, 255, 255, 0.01)
  );
  border: 1px solid rgba(255, 255, 255, 0.09);
  backdrop-filter: blur(12px);
  box-shadow: 0 35px 90px rgba(2, 6, 23, 0.7);
`;

/* ================= ICON ================= */

const IconWrap = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 22px;
  display: grid;
  place-items: center;
  margin-bottom: 28px;
  color: #fff;
  background: linear-gradient(135deg, #3b82f6, #06b6d4);
  box-shadow: 0 0 60px rgba(59, 130, 246, 0.5);
`;

/* ================= TEXT ================= */

const Title = styled.h3`
  font-size: 24px;
  font-weight: 900;
  margin-bottom: 14px;
  color: ${({ theme }) => theme.colors.text};
`;

const Text = styled.p`
  font-size: 16px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;
const TechSelector = styled.div`
  display: flex;
  justify-content: center;
  gap: 14px;
  margin-top: 60px;
  flex-wrap: wrap;
`;

const TechBtn = styled.button`
  padding: 12px 22px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid
    ${({ active }) =>
      active ? "rgba(59,130,246,0.6)" : "rgba(255,255,255,0.15)"};

  background: ${({ active }) =>
    active
      ? "linear-gradient(135deg,#3b82f6,#06b6d4)"
      : "rgba(255,255,255,0.04)"};

  color: ${({ active }) => (active ? "#fff" : "#cbd5f5")};

  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;
const MindMap = styled.div`
  position: relative;
  width: 100%;
  height: 760px;
  margin-top: 140px;

  @media (max-width: 1000px) {
    height: auto;
    display: flex;
    flex-direction: column;
    gap: 40px;
  }
`;

const CenterNode = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 46px 52px;
  border-radius: 34px;
  background: linear-gradient(135deg, #3b82f6, #06b6d4);
  color: white;
  font-size: 26px;
  font-weight: 900;
  box-shadow: 0 40px 120px rgba(59, 130, 246, 0.65);
  z-index: 5;

  @media (max-width: 1000px) {
    position: static;
    transform: none;
    align-self: center;
  }
`;

const Node = styled(Card)`
  position: absolute;
  width: 280px;
  z-index: 2;
  @media (max-width: 1000px) {
    position: static;
    width: 100%;
  }
`;
const ConnectorSVG = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 1;

  line {
    stroke: rgba(59, 130, 246, 0.45);
    stroke-width: 2;
    stroke-dasharray: 6 6;
    animation: dash 6s linear infinite;
  }

  @keyframes dash {
    to {
      stroke-dashoffset: -120;
    }
  }

  @media (max-width: 1000px) {
    display: none;
  }
`;
const Insights = styled(motion.div)`
  margin: 80px auto 0;
  max-width: 900px;

  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;

  @media (max-width: 1000px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const InsightCard = styled.div`
  padding: 16px 20px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(8px);
  min-width: 220px;
`;

const InsightTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  opacity: 0.7;
  margin-bottom: 6px;
`;

const InsightValue = styled.div`
  font-size: 16px;
  font-weight: 900;
  color: white;
`;

const CareerGrid = styled.div`
  max-width: 1100px;
  margin: 120px auto 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;

  @media (max-width: 1000px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

/* ================= ANIMATIONS ================= */
const SalarySection = styled.div`
  margin: 120px auto 0;
  max-width: 1100px;
`;

const SalaryGrid = styled.div`
  margin-top: 40px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const SalaryCard = styled(motion.div)`
  padding: 36px;
  border-radius: 28px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.045),
    rgba(255, 255, 255, 0.015)
  );
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 30px 80px rgba(2, 6, 23, 0.65);
`;

const SalaryLevel = styled.div`
  font-size: 14px;
  opacity: 0.7;
  margin-bottom: 12px;
`;

const SalaryValue = styled.div`
  font-size: 22px;
  font-weight: 900;
  margin-bottom: 6px;
`;

const SalaryRegion = styled.div`
  font-size: 14px;
  opacity: 0.8;
`;
const Sources = styled.div`
  margin-top: 28px;
  text-align: center;
  font-size: 14px;
  opacity: 0.75;

  a {
    color: #60a5fa;
    margin: 0 8px;
    text-decoration: none;
    font-weight: 600;
  }

  a:hover {
    text-decoration: underline;
  }
`;
const Highlight = styled.span`
  color: #60a5fa; /* Tailwind blue-400 type */
  font-weight: 800;
`;

const fadeUp = {
  hidden: { opacity: 0, y: 70 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, ease: "easeOut" },
  },
};

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.25 } },
};

/* ================= DATA ================= */
const TECHNOLOGIES = {
  javascript: {
    label: "JavaScript",
    subtitle: "Web, Mobile, Backend — one language, multiple career paths.",
    insights: {
      salary: {
        india: {
          beginner: "₹4–7 LPA",
          mid: "₹8–14 LPA",
          senior: "₹15–25 LPA",
        },
        global: {
          beginner: "$55k–75k",
          mid: "$80k–120k",
          senior: "$130k–180k",
        },
        sources: [
          {
            label: "Glassdoor",
            url: "https://www.glassdoor.co.in/Salaries/javascript-developer-salary-SRCH_KO0,20.htm",
          },
          {
            label: "Levels.fyi",
            url: "https://www.levels.fyi/roles/software-engineer",
          },
          {
            label: "Indeed",
            url: "https://in.indeed.com/career/javascript-developer/salaries",
          },
        ],
      },

      demand: {
        jobs: "120k+ openings",
        growth: "High",
      },

      products: ["Netflix", "Uber", "Airbnb", "PayPal", "Instagram"],
    },
    nodes: [
      {
        icon: Layout,
        title: "Frontend Development",
        text: "React, Next.js, UI & animations.",
        position: { top: "8%", left: "50%" },
      },
      {
        icon: Server,
        title: "Backend Development",
        text: "Node.js APIs & services.",
        position: { top: "50%", left: "6%" },
      },
      {
        icon: Smartphone,
        title: "Mobile Apps",
        text: "React Native apps.",
        position: { top: "50%", right: "6%" },
      },
      {
        icon: Briefcase,
        title: "Jobs & Freelancing",
        text: "Remote jobs, SaaS, freelancing.",
        position: { bottom: "6%", left: "50%" },
      },
    ],
  },

  python: {
    label: "Python",
    subtitle: "Automation, backend, data, AI.",
    insights: {
      salary: {
        india: {
          beginner: "₹4–8 LPA",
          mid: "₹9–16 LPA",
          senior: "₹18–30 LPA",
        },
        global: {
          beginner: "$60k–85k",
          mid: "$90k–135k",
          senior: "$140k–190k",
        },
        sources: [
          {
            label: "Glassdoor",
            url: "https://www.glassdoor.co.in/Salaries/python-developer-salary-SRCH_KO0,16.htm",
          },
          {
            label: "Indeed",
            url: "https://in.indeed.com/career/python-developer/salaries",
          },
          {
            label: "Levels.fyi",
            url: "https://www.levels.fyi/roles/software-engineer",
          },
        ],
      },

      demand: {
        jobs: "95k+ openings",
        growth: "Very High",
      },

      products: [
        "YouTube",
        "Instagram",
        "Dropbox",
        "Spotify",
        "Netflix (ML)",
        "OpenAI tools",
      ],
    },
    nodes: [
      {
        icon: Server,
        title: "Backend APIs",
        text: "Django, FastAPI systems.",
        position: { top: "8%", left: "50%" },
      },
      {
        icon: Code,
        title: "Automation",
        text: "Scripts, scraping, tooling.",
        position: { top: "50%", left: "6%" },
      },
      {
        icon: Cloud,
        title: "Data & AI",
        text: "ML, data pipelines.",
        position: { top: "50%", right: "6%" },
      },
      {
        icon: Briefcase,
        title: "Enterprise Jobs",
        text: "Analytics & backend teams.",
        position: { bottom: "6%", left: "50%" },
      },
    ],
  },

  java: {
    label: "Java",
    subtitle: "Enterprise-grade backend & Android.",
    insights: {
      salary: {
        india: {
          beginner: "₹5–9 LPA",
          mid: "₹10–18 LPA",
          senior: "₹20–35 LPA",
        },
        global: {
          beginner: "$65k–90k",
          mid: "$95k–140k",
          senior: "$150k–200k",
        },
        sources: [
          {
            label: "Glassdoor",
            url: "https://www.glassdoor.co.in/Salaries/java-developer-salary-SRCH_KO0,14.htm",
          },
          {
            label: "Indeed",
            url: "https://in.indeed.com/career/java-developer/salaries",
          },
          {
            label: "Levels.fyi",
            url: "https://www.levels.fyi/roles/software-engineer",
          },
        ],
      },

      demand: {
        jobs: "85k+ openings",
        growth: "High (Enterprise & Backend)",
      },

      products: [
        "Amazon",
        "LinkedIn",
        "Netflix",
        "Uber",
        "Banking Systems",
        "Android OS",
      ],
    },
    nodes: [
      {
        icon: Server,
        title: "Backend Systems",
        text: "Spring Boot & microservices.",
        position: { top: "8%", left: "50%" },
      },
      {
        icon: Smartphone,
        title: "Android",
        text: "Native Android apps.",
        position: { top: "50%", left: "6%" },
      },
      {
        icon: Cloud,
        title: "Enterprise Software",
        text: "Banking & finance systems.",
        position: { top: "50%", right: "6%" },
      },
      {
        icon: Briefcase,
        title: "Stable Jobs",
        text: "MNC & long-term roles.",
        position: { bottom: "6%", left: "50%" },
      },
    ],
  },

  c_cpp: {
    label: "C / C++",
    subtitle: "Foundation of systems, performance-critical software.",
    insights: {
      salary: {
        india: {
          beginner: "₹5–10 LPA",
          mid: "₹12–22 LPA",
          senior: "₹25–45 LPA",
        },
        global: {
          beginner: "$70k–95k",
          mid: "$100k–150k",
          senior: "$160k–230k",
        },
        sources: [
          {
            label: "Glassdoor",
            url: "https://www.glassdoor.co.in/Salaries/c-plus-plus-developer-salary-SRCH_KO0,21.htm",
          },
          {
            label: "Indeed",
            url: "https://in.indeed.com/career/c%2B%2B-developer/salaries",
          },
          {
            label: "Levels.fyi",
            url: "https://www.levels.fyi/roles/software-engineer",
          },
        ],
      },

      demand: {
        jobs: "40k+ openings",
        growth: "Medium (High Skill, High Pay)",
      },

      products: [
        "Windows Kernel",
        "Linux",
        "Unreal Engine",
        "Game Engines",
        "Databases (MySQL)",
        "Embedded Systems",
      ],
    },

    nodes: [
      {
        icon: Code,
        title: "System Programming",
        text: "Operating systems, memory management, compilers.",
        position: { top: "8%", left: "50%" },
      },
      {
        icon: Server,
        title: "Backend Engines",
        text: "High-performance servers, databases, game engines.",
        position: { top: "50%", left: "8%" },
      },
      {
        icon: Smartphone,
        title: "Embedded Systems",
        text: "IoT devices, hardware-level programming.",
        position: { top: "50%", right: "8%" },
      },
      {
        icon: Cloud,
        title: "Game & OS Core",
        text: "Unreal Engine, kernels, graphics pipelines.",
        position: { bottom: "6%", left: "50%" },
      },
    ],
  },
};

/* ================= COMPONENT ================= */

export default function TechnologyScopePage() {
  const [activeTech, setActiveTech] = useState("javascript");
  const tech = TECHNOLOGIES[activeTech];

  return (
    <Page>
      <Container>
        <Heading variants={fadeUp} initial="hidden" animate="show">
          {tech.label} — Technology Scope
        </Heading>

        <Sub variants={fadeUp} initial="hidden" animate="show">
          {tech.subtitle}
        </Sub>
        <Sub style={{ maxWidth: 900, opacity: 0.88 }}>
          These roles and salaries represent{" "}
          <Highlight>skill-based software development careers</Highlight>. They
          focus on building <Highlight>real-world products</Highlight> using
          this technology — not <Highlight>competitive programming</Highlight>{" "}
          or <Highlight>DSA-heavy interview tracks</Highlight>.
        </Sub>

        {/* 1️⃣ TECHNOLOGY SELECTOR */}
        <TechSelector>
          {Object.keys(TECHNOLOGIES).map((key) => (
            <TechBtn
              key={key}
              active={key === activeTech}
              onClick={() => setActiveTech(key)}
            >
              {TECHNOLOGIES[key].label}
            </TechBtn>
          ))}
        </TechSelector>

        {/* 2️⃣ SALARY GRID — 👈 YAHAN DALO */}
        <SalarySection>
          <Heading
            as="h2"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
          >
            Salary by Experience Level
          </Heading>

          <Sub>
            Approximate industry ranges based on Glassdoor, Indeed & Levels.fyi
          </Sub>

          <SalaryGrid>
            {/* Beginner */}
            <SalaryCard whileHover={{ y: -10 }}>
              <SalaryLevel>Beginner (0–2 yrs)</SalaryLevel>
              <SalaryValue>{tech.insights.salary.india.beginner}</SalaryValue>
              <SalaryRegion>India</SalaryRegion>
              <SalaryValue style={{ marginTop: 8 }}>
                {tech.insights.salary.global.beginner}
              </SalaryValue>
              <SalaryRegion>Global</SalaryRegion>
            </SalaryCard>

            {/* Mid */}
            <SalaryCard whileHover={{ y: -10 }}>
              <SalaryLevel>Mid-Level (2–5 yrs)</SalaryLevel>
              <SalaryValue>{tech.insights.salary.india.mid}</SalaryValue>
              <SalaryRegion>India</SalaryRegion>
              <SalaryValue style={{ marginTop: 8 }}>
                {tech.insights.salary.global.mid}
              </SalaryValue>
              <SalaryRegion>Global</SalaryRegion>
            </SalaryCard>

            {/* Senior */}
            <SalaryCard whileHover={{ y: -10 }}>
              <SalaryLevel>Experienced (5+ yrs)</SalaryLevel>
              <SalaryValue>{tech.insights.salary.india.senior}</SalaryValue>
              <SalaryRegion>India</SalaryRegion>
              <SalaryValue style={{ marginTop: 8 }}>
                {tech.insights.salary.global.senior}
              </SalaryValue>
              <SalaryRegion>Global</SalaryRegion>
            </SalaryCard>
          </SalaryGrid>

          {/* 3️⃣ SOURCES */}
          <Sources>
            Salary data sources:
            {tech.insights.salary.sources.map((s, i) => (
              <a key={i} href={s.url} target="_blank" rel="noopener noreferrer">
                {s.label}
              </a>
            ))}
          </Sources>
        </SalarySection>
        <Sub style={{ marginTop: 24, fontSize: 15, opacity: 0.85 }}>
          These salary ranges are typical for{" "}
          <Highlight>skill-focused roles</Highlight> such as{" "}
          <Highlight>frontend</Highlight>, <Highlight>backend</Highlight>,{" "}
          <Highlight>mobile</Highlight>, or{" "}
          <Highlight>systems engineers</Highlight>.
          <br />
          Salaries at <Highlight>top product companies</Highlight> (Google,
          Microsoft, Meta) usually require <Highlight>strong DSA</Highlight> +{" "}
          <Highlight>system design preparation</Highlight>.
        </Sub>

        {/* 4️⃣ CAREER PATHS */}
        <CareerGrid>
          {tech.nodes.map((node, i) => {
            const Icon = node.icon;
            return (
              <Card key={i} whileHover={{ y: -12 }}>
                <IconWrap>
                  <Icon size={30} />
                </IconWrap>
                <Title>{node.title}</Title>
                <Text>{node.text}</Text>
              </Card>
            );
          })}
        </CareerGrid>

        {/* TRUST NOTE */}
        <Sub style={{ marginTop: 80, fontSize: 14, opacity: 0.7 }}>
          *Salary & demand figures are approximate, based on Glassdoor, Indeed &
          Levels.fyi industry data.
        </Sub>
      </Container>
    </Page>
  );
}
